import type {ReactElement} from 'react';
import React, {useEffect} from 'react';
import {Panel, PanelHeader} from '@Components/panel';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {ContextualMenuToggleOption} from '@Components/contextual-menu/components/contextual-menu-toggle-option';
import {PageGridSizeOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/page-grid-size-option';
import {updateOptionsPanelHeight} from '@Components/poster-editor/library/poster-editor-library';
import {ContextualMenu} from '@Components/contextual-menu';
import {useAppSelector} from '@/hooks';

export interface PosterEditorGridPanelProps {
  panelId: string;
}

const GRID_PANEL_DEFAULT_HEIGHT = 114;
const GRID_PANEL_HEIGHT_ENABLED = 188;

function PosterEditorGridPanel({...props}: PosterEditorGridPanelProps): ReactElement | null {
  const onClose = useDefaultPanelClose(props.panelId);
  const isGridEnabled = useAppSelector((state) => {
    return state.posterEditor.posterObject?.grid.enabled;
  });

  useEffect((): void => {
    updateHeight();
  }, [isGridEnabled]);

  const updateHeight = (): void => {
    let height = GRID_PANEL_DEFAULT_HEIGHT;

    if (isGridEnabled) {
      height = GRID_PANEL_HEIGHT_ENABLED;
    }
    updateOptionsPanelHeight(props.panelId, `${height}px`);
  };

  if (isGridEnabled === undefined) {
    return null;
  }

  const getHeader = (): ReactElement => {
    return <PanelHeader onClose={onClose} title={window.i18next.t('pmwjs_grid')} />;
  };

  const getContent = (): ReactElement => {
    return (
      <ContextualMenu id="grid-mobile-options" hasMobileModalPadding hasMobileMorphingProperties>
        {isGridEnabled ? <PageGridSizeOption /> : null}
        <ContextualMenuToggleOption
          id="grid-toggle-button"
          className={isGridEnabled ? 'spacing-p-t-4' : ''}
          label={window.i18next.t('pmwjs_grid')}
          isChecked={isGridEnabled}
          onClick={(): void => {
            void window.posterEditor?.whiteboard?.updateFromObject(
              {
                grid: {
                  enabled: !isGridEnabled,
                },
              },
              {
                undoable: false,
              }
            );
          }}
        />
      </ContextualMenu>
    );
  };

  return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
}

export default PosterEditorGridPanel;
